<template>
  <!--
    SceneViewer component is used to display a 3D scene with a main product
    and user avatar data, enabling interactive product selection.

    The following props configure the SceneViewer component:
    - productlist: The complete list of products
    - product: The main product info
    - body: The user's avatar URL
    - bodyType: The user's body type (default: 'body_1')
    - avatarGender: The avatar's gender
    - createAvatar: A function for avatar creation
    - The component is typically shown only if a main product is present and "scene" view mode is active
  -->
  <div class="viewer-container">
    <!-- The Settings component; pass createAvatar as a prop -->
    <SettinsComponent :create-avatar="createAvatar" />

    <!-- A camera position display (optional), hidden by CSS below -->
    <div ref="cameraPositionDisplay" class="camera-position"></div>

    <!-- The container where Three.js will render the 3D scene -->
    <div id="container" ref="container"></div>

    <!-- Tooltip element, toggled by showTooltip -->
    <div ref="tooltip" class="tooltip" v-show="showTooltip">
      <p>{{ tooltipContent }}</p>
    </div>

    <!-- Hideable bottom list of recommended products -->
    <HideableBottomList
      ref="bottomList"
      v-if="mainProduct && !reloads"
      :toggled="listToggled"
      :product-selected="productSelected"
      :product-name="mainProduct.name"
      :recommended="recommended_products"
    />
  </div>
</template>

<script>
import SettinsComponent from "./SettinsComponent.vue";
import HideableBottomList from "./HideableBottomList.vue";

// Import our composables
import { loadModel, filterAnimation } from "@/composables/use3DModels.js";
import { createBillboard, showBillboard, hideBillboard, updateBillboardTexture } from "@/composables/useBillboard.js";
import { animateCamera, preventCameraPenetration } from "@/composables/useCamera.js";

import * as THREE from 'three';
import * as TWEEN from '@tweenjs/tween.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { markRaw } from 'vue';
import { ASSETS } from '@/constants/urls';
import { getAvatarModelPath, getModelUrl } from '@/constants/pathUtils';

/**
 * SceneViewer
 * 
 * A component that sets up a Three.js scene to display a user avatar and a main product,
 * with interactive "dots" for product selection and an optional billboard for product details.
 * 
 * @component
 */
export default {
  name: 'SceneViewer',
  components: {
    SettinsComponent,
    HideableBottomList
  },

  /**
   * Props:
   * @prop {String} body - URL of the user's avatar
   * @prop {String} avatarGender - Gender of the avatar
   * @prop {String} bodyType - The user's body type (default: 'body_1')
   * @prop {Function} createAvatar - A callback for creating the avatar
   * @prop {Object} product - The main product info
   * @prop {Array} productlist - A list of all products
   */
  props: {
    body: { type: String, default: null },
    avatarGender: { type: String, default: null },
    bodyType: { type: String, default: 'body_1' },
    createAvatar: Function,
    product: Object,
    productlist: Array
  },

  data() {
    return {
      /** Scene reloading flag */
      reloads: false,

      /** Three.js scene references */
      scene: null,
      renderer: null,
      camera: null,
      controls: null,
      mixer: null,
      clock: null,
      animationGroup: null,

      /** Billboard & logo references */
      loadLogo: null,
      billboard: null,
      billboardCanvas: null,
      billboardContext: null,
      billboardVisible: false,

      /** Avatar & product references */
      currentAvatar: null,
      walkAction: null,
      bodyModel: null,
      productModels: [],
      productDots: [],

      /** Tooltip properties */
      showTooltip: false,
      tooltipContent: '',

      /** Raycasting essentials */
      mouse: null,
      raycaster: null,
      rayLine: null,
      rayLineMaterial: null,

      /** Product data */
      mainProduct: null,
      products: [],
      recommended_products: [],

      /** Camera collision prevention flag */
      cameraCollisionFlag: false,

      /** Cached bounding sphere for collision checks */
      modelBoundingSphere: null,

      /** Named camera position presets */
      cameraPositions: {
        init:    { x: 0,    y: 3,   z: 5,   controlX: 0,   controlY: 0.5, controlZ: 0 },
        default: { x: 0.4,  y: 1.25,z: 3.78,controlX: 0,   controlY: 0.4, controlZ: 0 },
        face:    { x: 0.25, y: 1.18,z: 2.80,controlX: 0,   controlY: 0.8, controlZ: 0 },
        panel:   { x: -0.5, y: 1.38,z: 3,   controlX: 0.2, controlY: 1,   controlZ: 0.8 }
      },
      
      /** Asset constants */
      ASSETS
    };
  },

  computed: {
    /**
     * Decides which avatar model to load, based on user's body/gender or default male/female
     * @returns {string} Path to the avatar model
     */
     current_body() {
      const gender = (this.mainProduct && this.mainProduct.gender === 'erkek') ? 'erkek' : 'kadın';
      console.log('Product gender:', this.mainProduct?.gender);
      console.log('Avatar gender:', this.avatarGender);
      console.log('Selected gender for model:', gender);
      const path = getAvatarModelPath(this.body, this.avatarGender, gender);
      console.log('Selected avatar path:', path);
      return path;
    }
  },

  mounted() {
    // Initialize component with sequence of setup steps
    this.setupInitialState();
  },

  beforeUnmount() {
    // Clean up event listeners and resources
    this.cleanupResources();
  },

  methods: {
    /**
     * Sets up the initial component state
     */
    setupInitialState() {
      // 1) Set up main product & recommended list
      this.mainProduct = this.product;
      this.products = [];
      this.setupRecommendedProducts();

      // 2) Add a logo model
      this.addLogo();

      // 3) Initialize Three.js
      this.initThree();

      // 4) Load the user avatar body
      this.loadBody();

      // 5) Create a billboard group
      this.setupBillboard();

      // 6) Listen for clicks to check if user interacts with a product dot
      window.addEventListener('click', this.onMouseClick, false);
    },

    /**
     * Prepares the recommended products list
     */
    setupRecommendedProducts() {
      Object.keys(this.productlist).forEach(key => {
        const pr = this.productlist[key];
        pr.loaded = false;
        pr.loading = false;
        this.recommended_products.push(pr);
      });
    },

    /**
     * Creates and sets up the product billboard
     */
    setupBillboard() {
      this.billboard = createBillboard(this.scene);
      this.billboardCanvas = document.createElement('canvas');
      this.billboardCanvas.width = 256;
      this.billboardCanvas.height = 512;
      this.billboardContext = this.billboardCanvas.getContext('2d');
      this.billboard.children[0].material.map = new THREE.CanvasTexture(this.billboardCanvas);
    },

    /**
     * Cleans up resources when component is unmounted
     */
    cleanupResources() {
      window.removeEventListener('resize', this.onWindowResize);
      window.removeEventListener('click', this.onMouseClick);

      if (this.renderer) {
        this.renderer.dispose();
      }
    },

    /**
     * Animates the camera smoothly to a new position and target
     * @param {THREE.Vector3} newPosition - Target camera position
     * @param {THREE.Vector3} newControlsTarget - Target controls look-at point
     */
    animateCamera(newPosition, newControlsTarget) {
      animateCamera(this.camera, this.controls, newPosition, newControlsTarget, 1000);
    },

    /**
     * Prevents camera from penetrating the model using raycasting
     */
    preventCameraPenetration() {
      preventCameraPenetration({
        camera: this.camera,
        controls: this.controls,
        bodyModel: this.bodyModel,
        productModels: this.productModels,
        recursionFlagRef: { value: this.cameraCollisionFlag }
      });
    },

    /**
     * Handles product selection - either loads or removes a product
     * @param {Object} product - The product to select/deselect
     */
    productSelected(product) {
      if (product.loaded) {
        this.removeProduct(product);
      } else {
        this.findProductsByBodyType(product.body_part, product.priority);
        this.loadProduct(product);
      }
    },

    /**
     * Ensures only one product of the same body part/priority is displayed
     * @param {string} body_part - The body part targeted by the product
     * @param {number} priority - The priority level of the product
     */
    findProductsByBodyType(body_part, priority) {
      Object.entries(this.productModels).forEach(([key]) => {
        const p = this.products[key];
        if (!p) return;

        const sameBody = (p.body_part === body_part && p.priority === priority);
        const fullCoversOthers = (
          (body_part === "full body" && (p.body_part === "üst beden" || p.body_part === "alt beden") && p.priority === priority) ||
          (p.body_part === "full body" && (body_part === "üst beden" || body_part === "alt beden") && p.priority === priority)
        );
        if (sameBody || fullCoversOthers) {
          this.productSelected(p);
        }
      });
    },

    /**
     * Gets the model URL for a product based on body type
     * @param {Object} product - The product object
     * @returns {string} The URL for the model
     */
    getModelUrl(product) {
      return getModelUrl(product, this.bodyType);
    },

    /**
     * Moves camera to a predefined position
     * @param {string} posKey - The position key ('init', 'face', 'panel', etc.)
     */
    setCamera(posKey) {
      const preset = this.cameraPositions[posKey];
      if (!preset) return;

      const newPos = new THREE.Vector3(preset.x, preset.y, preset.z);
      const newTgt = new THREE.Vector3(preset.controlX, preset.controlY, preset.controlZ);

      // Temporarily disable collision detection during camera movement
      const oldFlag = this.cameraCollisionFlag;
      this.cameraCollisionFlag = true;

      this.animateCamera(newPos, newTgt);

      // Restore collision detection after animation
      setTimeout(() => {
        this.cameraCollisionFlag = oldFlag;
      }, 1100);
    },

    /**
     * Loads a product's 3D model and adds it to the scene
     * @param {Object} product - The product to load
     */
    loadProduct(product) {
      product.loading = true;
      this.products[product._id] = product;

      const url = this.getModelUrl(product);
      loadModel(url, (gltf) => {
        const model = gltf.scene;
        this.scene.add(model);

        // Configure model mesh properties
        this.setupModelMeshes(model);

        this.animationGroup.add(model);
        this.productModels[product._id] = model;

        // Create a dot for the product
        this.productDots[product._id] = this.addDot(product);

        // Update product state
        product.loading = false;
        product.loaded = true;
        this.products[product._id] = product;
      });
    },

    /**
     * Sets up basic mesh properties for a loaded model
     * @param {THREE.Object3D} model - The loaded 3D model
     */
    setupModelMeshes(model) {
      model.traverse((obj) => {
        if (obj.isMesh) {
          obj.castShadow = true;
          obj.receiveShadow = true;
          obj.material.envMapIntensity = 0.3;
        }
      });
    },

    /**
     * Handles product list toggle event
     * @param {boolean} value - Whether the list is open
     */
    listToggled(value) {
      if (!value && !this.billboardVisible) {
        this.setCamera('face');
      } else if (value) {
        this.setCamera('default');
        if (this.billboardVisible) {
          hideBillboard(this.billboard, { value: this.billboardVisible });
        }
      }
    },

    /**
     * Removes a product from the scene
     * @param {Object} product - The product to remove
     */
    removeProduct(product) {
      const model = this.productModels[product._id];
      if (!model) return;

      // Remove model from scene
      this.scene.remove(model);
      this.scene.children = this.scene.children.filter(child => child.uuid !== model.uuid);

      // Clean up resources
      this.disposeModelResources(model);

      // Remove from tracking collections
      delete this.productModels[product._id];
      this.removeDot(product._id);

      // Update product state
      product.loading = false;
      product.loaded = false;
      this.products[product._id] = product;
      this.scene.updateMatrixWorld();
    },

    /**
     * Disposes of model resources to prevent memory leaks
     * @param {THREE.Object3D} model - The 3D model to clean up
     */
    disposeModelResources(model) {
      model.traverse((obj) => {
        if (obj.isMesh) {
          obj.geometry.dispose();
          obj.material.dispose();
          if (obj.material.map) {
            obj.material.map.dispose();
          }
        }
      });
    },

    /**
     * Loads the user's avatar body model
     */
    loadBody() {
      loadModel(this.current_body, (gltf) => {
        const model = gltf.scene;
        this.setupModelMeshes(model);

        this.bodyModel = model;
        this.animationGroup.add(model);
        this.hideLogo();
        this.scene.add(model);

        // Move camera to face position
        this.setCamera('face');

        // Load the main product on this avatar
        this.loadProduct(this.mainProduct);
      });
    },

    /**
     * Loads and adds the logo model to the scene
     */
    addLogo() {
      loadModel(ASSETS.MODELS.LOGO, (gltf) => {
        const model = gltf.scene;
        const clip = filterAnimation(gltf.animations[0]);
        const action = this.mixer.clipAction(clip);

        action.play();
        model.scale.set(0, 0, 0);
        model.position.y = 0.5;
        this.loadLogo = model;
        this.scene.add(model);

        this.showLogo();
      });
    },

    /**
     * Animates the logo to scale in
     */
    showLogo() {
      new TWEEN.Tween(this.loadLogo.scale)
        .to({ x: 0.25, y: 0.25, z: 0.25 }, 1000)
        .easing(TWEEN.Easing.Exponential.Out)
        .start();
      this.billboardVisible = true;
    },

    /**
     * Animates the logo to scale out
     */
    hideLogo() {
      new TWEEN.Tween(this.loadLogo.scale)
        .to({ x: 0, y: 0, z: 0 }, 1000)
        .easing(TWEEN.Easing.Exponential.In)
        .start();
      this.billboardVisible = false;
    },

    /**
     * Initializes the Three.js environment
     */
    initThree() {
      const container = this.$refs.container;

      // Initialize core Three.js objects
      this.initializeThreeCore();
      
      // Set up camera
      this.setupCamera(container);
      
      // Create scene and lighting
      this.setupSceneAndLights();
      
      // Create renderer
      this.setupRenderer(container);
      
      // Set up orbit controls
      this.setupOrbitControls();
      
      // Add event listener for window resize
      window.addEventListener('resize', this.onWindowResize);
      
      // Set initial camera position
      this.setCamera('init');
      
      // Load idle animation
      this.loadIdleAnimation();
      
      // Create ground plane
      this.createGroundPlane();
      
      // Start animation loop
      this.animate();
    },

    /**
     * Initializes core Three.js objects
     */
    initializeThreeCore() {
      this.clock = markRaw(new THREE.Clock());
      this.mouse = markRaw(new THREE.Vector2());
      this.raycaster = markRaw(new THREE.Raycaster());
      this.animationGroup = markRaw(new THREE.AnimationObjectGroup());
      this.mixer = markRaw(new THREE.AnimationMixer(this.animationGroup));
    },

    /**
     * Sets up the camera
     * @param {HTMLElement} container - The container element
     */
    setupCamera(container) {
      this.camera = markRaw(new THREE.PerspectiveCamera(
        45,
        container.clientWidth / container.clientHeight,
        1,
        1000
      ));
      this.camera.lookAt(new THREE.Vector3(-100, 1, 4));
    },

    /**
     * Sets up the scene and lighting
     */
    setupSceneAndLights() {
      // Create scene
      this.scene = markRaw(new THREE.Scene());
      this.scene.background = new THREE.Color(0xfcfcfc);
      this.scene.fog = new THREE.Fog(0xcccccc, 30, 90);

      // Add hemisphere lights
      const hemiLight = new THREE.HemisphereLight(0xffffff, 0xffffff);
      hemiLight.position.set(10, 20, 0);
      this.scene.add(hemiLight);

      const hemiLight2 = new THREE.HemisphereLight(0xffe5cc, 0xffffff);
      hemiLight2.position.set(-10, -20, 0);
      this.scene.add(hemiLight2);

      // Add directional light
      const dirLight = new THREE.DirectionalLight(0xffffff);
      dirLight.position.set(-3, 3, 3);
      dirLight.castShadow = true;
      this.scene.add(dirLight);

      // Add spot light
      const spotLight = new THREE.SpotLight(0xffffff, 1);
      spotLight.position.set(0, 2, 2);
      spotLight.target.position.set(0, 0, 0);
      spotLight.angle = Math.PI / 4;
      spotLight.penumbra = 0.5;
      this.scene.add(spotLight);
      this.scene.add(spotLight.target);

      // Add face light
      const faceLight = new THREE.HemisphereLight(0xcccccc, 0xffffff, 0.5);
      faceLight.position.set(0, 5, 5);
      this.scene.add(faceLight);
    },

    /**
     * Sets up the renderer
     * @param {HTMLElement} container - The container element
     */
    setupRenderer(container) {
      this.renderer = new THREE.WebGLRenderer({ antialias: true });
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.renderer.setSize(container.clientWidth, container.clientHeight);
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.0;
      container.appendChild(this.renderer.domElement);
    },

    /**
     * Sets up orbit controls for camera navigation
     */
    setupOrbitControls() {
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.maxPolarAngle = Math.PI / 1.85;
      this.controls.minPolarAngle = 0;
      this.controls.minDistance = 2.0;
      this.controls.maxDistance = 10;
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.05;
      this.controls.rotateSpeed = 0.5;
      this.controls.zoomSpeed = 0.5;
      this.controls.enablePan = false;
    },

    /**
     * Loads the idle animation for the avatar
     */
    loadIdleAnimation() {
      loadModel(ASSETS.MODELS.IDLE, (gltf) => {
        const clip = filterAnimation(gltf.animations[0]);
        const action = this.mixer.clipAction(clip);
        this.walkAction = action;
        this.walkAction.play();
      });
    },

    /**
     * Creates the ground plane for the scene
     */
    createGroundPlane() {
      const groundGeom = new THREE.CircleGeometry(50, 32);
      const groundMat = new THREE.MeshPhongMaterial({
        color: 0xfefefe,
        depthWrite: false
      });
      const ground = new THREE.Mesh(groundGeom, groundMat);
      ground.rotation.x = -Math.PI / 2;
      ground.receiveShadow = true;
      ground.position.y += 0.045;
      this.scene.add(ground);
    },

    /**
     * Handles window resize events
     */
    onWindowResize() {
      const container = this.$refs.container;
      this.camera.aspect = container.clientWidth / container.clientHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(container.clientWidth, container.clientHeight);
    },

    /**
     * Main animation loop
     */
    animate() {
      requestAnimationFrame(this.animate);
      const delta = this.clock.getDelta();

      // Update animation mixer if available
      if (this.mixer) {
        this.mixer.update(delta);
      }
      
      // Update controls if damping is enabled
      if (this.controls.enableDamping) {
        this.controls.update();
      }

      // Prevent camera penetration if body model exists
      if (this.bodyModel) {
        this.preventCameraPenetration();
      }

      // Update tweens and render the scene
      TWEEN.update();
      this.renderer.render(this.scene, this.camera);
    },

    /**
     * Handles clicking on a product dot
     */
    handleClickOnDot() {
      if (!this.billboardVisible) {
        showBillboard(this.billboard, { value: this.billboardVisible });
      }
      this.$refs.bottomList.closePanel();
      this.setCamera('panel');
    },

    /**
     * Handles hovering over a product dot
     * @param {THREE.Object3D} dotObject - The dot object being hovered
     */
    handleHover(dotObject) {
      updateBillboardTexture({
        product: dotObject.userData.product,
        billboardMesh: this.billboard.children[0],
        billboardCanvas: this.billboardCanvas,
        billboardContext: this.billboardContext
      });
      this.handleClickOnDot();
    },

    /**
     * Handles mouse click events for interaction with product dots
     * @param {MouseEvent} event - The mouse click event
     */
    onMouseClick(event) {
      const rect = this.$refs.container.getBoundingClientRect();
      const mouseX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const mouseY = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      this.raycaster.setFromCamera({ x: mouseX, y: mouseY }, this.camera);

      const dots = Object.values(this.productDots);
      const intersects = this.raycaster.intersectObjects(dots);

      if (intersects.length > 0 && intersects[0].object.userData.product) {
        this.handleHover(intersects[0].object);
      }
    },

    /**
     * Creates a dot to represent a product's position on the avatar
     * @param {Object} product - The product to create a dot for
     * @returns {THREE.Mesh} The created dot mesh
     */
    addDot(product) {
      const sphereGeometry = new THREE.SphereGeometry(0.04, 32, 32);
      const sphereMaterial = new THREE.MeshBasicMaterial({ color: 0x333333 });
      const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
      sphere.userData.product = product;

      // Position dot based on body part
      this.positionDotByBodyPart(sphere, product.body_part);
      
      this.scene.add(sphere);
      return sphere;
    },

    /**
     * Positions a dot based on the body part it represents
     * @param {THREE.Mesh} sphere - The dot mesh
     * @param {string} bodyPart - The body part
     */
    positionDotByBodyPart(sphere, bodyPart) {
      switch ((bodyPart || '').trim()) {
        case 'üst beden':
          sphere.position.set(0.33, 1.45, 0);
          break;
        case 'alt beden':
          sphere.position.set(0.33, 0.8, 0);
          break;
        case 'kafa':
          sphere.position.set(0.33, 1.55, 0);
          break;
        case 'ayak':
        case 'ayakkabı':
          sphere.position.set(0.33, 0, 0);
          break;
        case 'el':
          sphere.position.set(0.33, 1, 0);
          break;
        case 'full body':
          sphere.position.set(0.33, 1, 0);
          break;
        default:
          sphere.position.set(0.33, -1, 0);
          break;
      }
    },

    /**
     * Removes a product dot from the scene
     * @param {string} productId - The ID of the product whose dot should be removed
     */
    removeDot(productId) {
      const dot = this.productDots[productId];
      if (dot) {
        this.scene.remove(dot);
        this.scene.children = this.scene.children.filter(child => child.uuid !== dot.uuid);
        dot.geometry.dispose();
        dot.material.dispose();
        delete this.productDots[productId];
        this.scene.updateMatrixWorld();
      }
    }
  }
};
</script>

<style scoped>
.viewer-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

/* Currently hidden to avoid showing camera coordinates text */
.camera-position {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 9999;
  color: #333;
  display: none !important;
}

.tooltip {
  position: absolute;
  top: 50px;
  left: 50px;
  background: #ffffffcc;
  border: 1px solid #ccc;
  padding: 1em;
  z-index: 9999;
  max-width: 200px;
  font-size: 14px;
}
</style>